import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const COUPON_LIST_QUERY = `
  query GetCouponList($input: JSON!) {
    GetCouponList(input: $input)
  }
`;
const COUPON_CREATE_MUTATION = `
  mutation CreateCoupon($input: JSON!) {
    CreateCoupon(input: $input)
  }
`;
const COUPON_ISSUE_MUTATION = `
  mutation IssueCoupon($input: JSON!) {
    IssueCoupon(input: $input)
  }
`;
const COUPON_CODE_LIST_QUERY = `
  query GetCouponCodeList($input: JSON!) {
    GetCouponCodeList(input: $input)
  }
`;
const CRM_API_BASE_URL = "https://crm-mqtt-718408119422.asia-east1.run.app";

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const resolveBackendBase = (graphqlEndpoint) => {
  const base = trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};

const buildApiUrl = (base, path, params = {}) => {
  const url = new URL(path, base);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const initialFormState = {
  code: "",
  distributionType: "manual",
  name: "",
  type: "cash",
  value: "",
  minSpend: "",
  maxDiscount: "",
  isActive: true,
  maxRedemptions: "",
  validFrom: "",
  validUntil: "",
  products: [],
  venueIds: [],
  machineIds: [],
  tags: "",
};

const initialImportFormState = {
  externalCampaignId: "",
  distributionType: "manual",
  name: "",
  value: "",
  minSpend: "",
  isActive: true,
  maxRedemptions: "",
  validFrom: "",
  validUntil: "",
  venueIds: [],
  machineIds: [],
  products: [],
  tags: "",
  remark: "",
  codes: [],
};

const initialFilterState = {
  code: "",
  validFrom: "",
  validUntil: "",
  isActiveOnly: false,
  orderBy: "validFrom_desc",
  page: "0",
  size: "50",
};
const initialCodeFilterState = {
  codes: "",
  start: "",
  end: "",
  orderBy: "DESC",
  page: "0",
  size: "50",
};

const ORDER_BY_OPTIONS = [
  { value: "validFrom_desc", label: "開始時間（新到舊）" },
  { value: "validFrom_asc", label: "開始時間（舊到新）" },
  { value: "validUntil_desc", label: "結束時間（新到舊）" },
  { value: "validUntil_asc", label: "結束時間（舊到新）" },
  { value: "value_desc", label: "優惠價值（高到低）" },
  { value: "value_asc", label: "優惠價值（低到高）" },
  { value: "uses_desc", label: "使用人數（高到低）" },
  { value: "uses_asc", label: "使用人數（低到高）" },
];
const CODE_ORDER_OPTIONS = [
  { value: "DESC", label: "新到舊 (DESC)" },
  { value: "ASC", label: "舊到新 (ASC)" },
];

const downloadIssuedCouponXlsx = (couponCode, issuedCoupons) => {
  const rows = issuedCoupons.map((coupon) => [couponCode, coupon]);
  const worksheet = XLSX.utils.aoa_to_sheet([["優惠券", "優惠券代碼"], ...rows]);
  const workbook = XLSX.utils.book_new();
  const filenameCouponCode = String(couponCode).replace(/[\\/:*?"<>|]+/g, "_") || "coupon";

  XLSX.utils.book_append_sheet(workbook, worksheet, "Issued Coupons");
  XLSX.writeFile(workbook, `coupons-${filenameCouponCode}.xlsx`);
};

const normalizeImportHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const createCouponFormSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    lineHeight: 1.2,
  },
  "& .MuiInputBase-root": {
    minHeight: 56,
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    height: "auto",
    padding: "16.5px 14px",
    fontSize: "0.95rem",
  },
  "& .MuiInputBase-input::placeholder": {
    fontSize: "0.95rem",
    opacity: 1,
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.9rem",
  },
};

const IssueButtonCell = ({ row, onIssue }) => {
  const canIssueVoucher =
    row?.original?.canIssueVoucher === true ||
    row?.original?.canIssueVoucher === "true" ||
    row?.original?.canIssueVoucher === 1 ||
    row?.original?.canIssueVoucher === "1";

  return (
    <Button
      variant="contained"
      size="small"
      disabled={!canIssueVoucher}
      onClick={() => {
        if (!canIssueVoucher) return;
        onIssue(row.original.code);
      }}
      sx={{
        backgroundColor: "#2e7d32",
        color: "#ffffff",
        fontWeight: 600,
        "&.Mui-disabled": {
          backgroundColor: "#D9D9D9",
          color: "#8A8A8A",
          cursor: "not-allowed",
        },
        "&:hover": { backgroundColor: "#1b5e20" },
        "&.Mui-disabled:hover": {
          backgroundColor: "#D9D9D9",
        },
      }}
    >
      發放優惠券
    </Button>
  );
};

IssueButtonCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      canIssueVoucher: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  onIssue: PropTypes.func.isRequired,
};

function CouponManagementBK() {
  const { t } = useTranslation("common");
  const { userData, merchantId, merchantTags } = useUserContext() || {};
  const backendUrl = React.useMemo(getGraphQLEndpoint, []);
  const backendBaseUrl = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const resolvedMerchantId = useMemo(() => {
    if (Array.isArray(merchantTags) && merchantTags.length) return merchantTags[0];
    return String(merchantId || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)[0];
  }, [merchantId, merchantTags]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [importForm, setImportForm] = useState(initialImportFormState);
  const [submitting, setSubmitting] = useState(false);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [filters, setFilters] = useState(initialFilterState);
  const [issueDialog, setIssueDialog] = useState({ open: false, code: "", amount: "" });
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [codeFilters, setCodeFilters] = useState(initialCodeFilterState);
  const [codeRows, setCodeRows] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
  const [voucherOptions, setVoucherOptions] = useState({ venues: [], products: [] });
  const [loadingVoucherOptions, setLoadingVoucherOptions] = useState(false);
  const [voucherOptionsError, setVoucherOptionsError] = useState("");
  const [importFileName, setImportFileName] = useState("");

  const handleOpenDialog = () => setOpenCreateDialog(true);
  const handleCloseDialog = () => {
    if (submitting) return;
    setOpenCreateDialog(false);
    setForm(initialFormState);
  };

  const resetImportForm = useCallback(() => {
    setImportForm(initialImportFormState);
    setImportFileName("");
  }, []);

  const handleOpenImportDialog = () => setOpenImportDialog(true);
  const handleCloseImportDialog = () => {
    if (importSubmitting) return;
    setOpenImportDialog(false);
    resetImportForm();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (key) => (_, values) => {
    setForm((prev) => ({
      ...prev,
      [key]: Array.isArray(values) ? values.map((item) => item.value) : [],
    }));
  };

  const handleToggle = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImportChange = (event) => {
    const { name, value } = event.target;
    setImportForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImportToggle = (event) => {
    const { name, checked } = event.target;
    setImportForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImportMultiSelectChange = (key) => (_, values) => {
    setImportForm((prev) => ({
      ...prev,
      [key]: Array.isArray(values) ? values.map((item) => item.value) : [],
    }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterToggle = (event) => {
    const { name, checked } = event.target;
    setFilters((prev) => ({ ...prev, [name]: checked }));
  };

  const handleCodeFilterChange = (event) => {
    const { name, value } = event.target;
    setCodeFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenIssueDialog = useCallback(
    (code) => setIssueDialog({ open: true, code: code || "", amount: "" }),
    []
  );
  const handleCloseIssueDialog = () => setIssueDialog({ open: false, code: "", amount: "" });
  const handleIssueAmountChange = (event) =>
    setIssueDialog((prev) => ({ ...prev, amount: event.target.value }));

  const formatDateTime = (value) =>
    value ? `${value.length === 16 ? `${value}:00` : value}+08:00` : "";

  const normalizeDateOnly = (value) => {
    if (!value) return "";
    return String(value).slice(0, 10);
  };

  const formatDisplayDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  };

  const venueOptions = useMemo(
    () =>
      (Array.isArray(voucherOptions.venues) ? voucherOptions.venues : [])
        .map((item) => {
          const venue = item?.venue || {};
          const value = venue?.id ? String(venue.id) : "";
          if (!value) return null;
          return {
            value,
            label: venue?.name || value,
            name: venue?.name || value,
            machines: Array.isArray(item?.machines) ? item.machines : [],
          };
        })
        .filter(Boolean),
    [voucherOptions.venues]
  );

  const allMachineOptions = useMemo(() => {
    const seen = new Set();
    return venueOptions
      .flatMap((item) => item.machines || [])
      .map((machine) => {
        const value = machine?.ref ? String(machine.ref) : "";
        if (!value || seen.has(value)) return null;
        seen.add(value);
        const name = machine?.name || machine?.machine_name || machine?.machine_code || value;
        return {
          value,
          label: name,
          name,
        };
      })
      .filter(Boolean);
  }, [venueOptions]);

  const machineOptions = useMemo(() => {
    if (!form.venueIds.length) return allMachineOptions;
    const allowedMachineIds = new Set(
      venueOptions
        .filter((item) => form.venueIds.includes(item.value))
        .flatMap((item) => item.machines || [])
        .map((machine) => String(machine?.ref || "").trim())
        .filter(Boolean)
    );
    return allMachineOptions.filter((option) => allowedMachineIds.has(option.value));
  }, [allMachineOptions, form.venueIds, venueOptions]);

  const importMachineOptions = useMemo(() => {
    if (!importForm.venueIds.length) return allMachineOptions;
    const allowedMachineIds = new Set(
      venueOptions
        .filter((item) => importForm.venueIds.includes(item.value))
        .flatMap((item) => item.machines || [])
        .map((machine) => String(machine?.ref || "").trim())
        .filter(Boolean)
    );
    return allMachineOptions.filter((option) => allowedMachineIds.has(option.value));
  }, [allMachineOptions, importForm.venueIds, venueOptions]);

  const productOptions = useMemo(
    () =>
      (Array.isArray(voucherOptions.products) ? voucherOptions.products : [])
        .map((item) => {
          const value = item?.product_id || item?.barcode || item?.ref || item?.id;
          if (!value) return null;
          const normalizedValue = String(value);
          const name = item?.label || item?.display_name || normalizedValue;
          return {
            value: normalizedValue,
            label: name,
            name,
          };
        })
        .filter(Boolean),
    [voucherOptions.products]
  );

  const selectedVenueOptions = useMemo(
    () => venueOptions.filter((option) => form.venueIds.includes(option.value)),
    [form.venueIds, venueOptions]
  );
  const selectedMachineOptions = useMemo(
    () => machineOptions.filter((option) => form.machineIds.includes(option.value)),
    [form.machineIds, machineOptions]
  );
  const selectedProductOptions = useMemo(
    () => productOptions.filter((option) => form.products.includes(option.value)),
    [form.products, productOptions]
  );

  const selectedImportVenueOptions = useMemo(
    () => venueOptions.filter((option) => importForm.venueIds.includes(option.value)),
    [importForm.venueIds, venueOptions]
  );
  const selectedImportMachineOptions = useMemo(
    () => importMachineOptions.filter((option) => importForm.machineIds.includes(option.value)),
    [importForm.machineIds, importMachineOptions]
  );
  const selectedImportProductOptions = useMemo(
    () => productOptions.filter((option) => importForm.products.includes(option.value)),
    [importForm.products, productOptions]
  );

  useEffect(() => {
    if (!machineOptions.length) {
      if (form.machineIds.length) {
        setForm((prev) => ({ ...prev, machineIds: [] }));
      }
      return;
    }

    const validMachineIds = new Set(machineOptions.map((option) => option.value));
    const nextMachineIds = form.machineIds.filter((value) => validMachineIds.has(value));
    if (nextMachineIds.length !== form.machineIds.length) {
      setForm((prev) => ({ ...prev, machineIds: nextMachineIds }));
    }
  }, [form.machineIds, machineOptions]);

  useEffect(() => {
    if (!importMachineOptions.length) {
      if (importForm.machineIds.length) {
        setImportForm((prev) => ({ ...prev, machineIds: [] }));
      }
      return;
    }

    const validMachineIds = new Set(importMachineOptions.map((option) => option.value));
    const nextMachineIds = importForm.machineIds.filter((value) => validMachineIds.has(value));
    if (nextMachineIds.length !== importForm.machineIds.length) {
      setImportForm((prev) => ({ ...prev, machineIds: nextMachineIds }));
    }
  }, [importForm.machineIds, importMachineOptions]);

  const fetchVoucherOptions = useCallback(async () => {
    if (!mdmAuthKey || !email) return;
    setLoadingVoucherOptions(true);
    setVoucherOptionsError("");
    try {
      const url = buildApiUrl(CRM_API_BASE_URL, "/api/v1/voucher/options", {
        email,
      });
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Mdm-Auth": mdmAuthKey,
          "Content-Type": "application/json",
        },
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `載入條件選單失敗 (${response.status})`);
      }
      if (Number(json?.code) !== 200) {
        throw new Error(json?.message || "載入條件選單失敗");
      }
      setVoucherOptions({
        venues: Array.isArray(json?.data?.venues) ? json.data.venues : [],
        products: Array.isArray(json?.data?.products) ? json.data.products : [],
      });
    } catch (error) {
      console.error("Fetch voucher options failed:", error);
      setVoucherOptionsError(error.message || "載入條件選單失敗");
    } finally {
      setLoadingVoucherOptions(false);
    }
  }, [email, mdmAuthKey, resolvedMerchantId]);

  const fetchCouponCodes = useCallback(async () => {
    const startValue = normalizeDateOnly(codeFilters.start);
    const endValue = normalizeDateOnly(codeFilters.end);
    const codes = codeFilters.codes
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const pageValue = Number(codeFilters.page);
    const sizeValue = Number(codeFilters.size);
    const payload = {
      email,
      codes,
      start: startValue || undefined,
      end: endValue || undefined,
      orderBy: codeFilters.orderBy || "DESC",
      page: Number.isFinite(pageValue) ? pageValue : 0,
      size: Number.isFinite(sizeValue) ? sizeValue : undefined,
    };

    setLoadingCodes(true);
    setCodeError("");
    try {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: COUPON_CODE_LIST_QUERY,
          variables: { input: payload },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API 回應錯誤：${response.status}`);
      }

      const data = await response.json();
      if (data?.errors?.length) {
        throw new Error(data.errors[0]?.message || "查詢失敗");
      }
      const result = data?.data?.GetCouponCodeList;
      const success =
        result?.code === 200 ||
        result?.code === "200" ||
        result?.code === 0 ||
        result?.code === "0";
      if (!success) {
        throw new Error(result?.message || "查詢失敗");
      }
      const list = result?.body?.data ?? result?.data ?? [];
      setCodeRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Fetch coupon code list failed:", error);
      setCodeError(error.message || "查詢失敗");
      setCodeRows([]);
    } finally {
      setLoadingCodes(false);
    }
  }, [backendUrl, codeFilters, email]);

  const fetchCoupons = useCallback(async () => {
    const pageValue = Number(filters.page);
    const resolvedPage = Number.isFinite(pageValue) ? pageValue : 0;
    const startValue = normalizeDateOnly(filters.validFrom) || "2024-01-01";
    const endValue = normalizeDateOnly(filters.validUntil) || "2027-12-31";
    const sizeValue = Number(filters.size);
    const payload = {
      email,
      isActive: filters.isActiveOnly ? true : undefined,
      start: startValue,
      end: endValue,
      orderBy: filters.orderBy,
      page: resolvedPage,
      size: Number.isFinite(sizeValue) ? sizeValue : 50,
    };

    setLoadingCoupons(true);
    setCouponError("");
    try {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: COUPON_LIST_QUERY,
          variables: { input: payload },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API 回應錯誤：${response.status}`);
      }

      const data = await response.json();
      if (data?.errors?.length) {
        throw new Error(data.errors[0]?.message || "查詢失敗");
      }
      const result = data?.data?.GetCouponList;
      const success =
        result?.code === 200 ||
        result?.code === "200" ||
        result?.code === 0 ||
        result?.code === "0";
      if (!success) {
        throw new Error(result?.message || "查詢失敗");
      }
      const list = result?.body?.data ?? result?.data ?? [];
      setCoupons(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Fetch coupon list failed:", error);
      setCouponError(error.message || "查詢失敗");
      setCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  }, [backendUrl, email, filters]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    fetchCouponCodes();
  }, [fetchCouponCodes]);

  useEffect(() => {
    if (!openCreateDialog && !openImportDialog) return;
    fetchVoucherOptions();
  }, [fetchVoucherOptions, openCreateDialog, openImportDialog]);

  const couponColumns = React.useMemo(
    () => [
      { Header: "活動代碼", accessor: "code" },
      { Header: "活動名稱", accessor: "name" },
      { Header: "發放類型", accessor: "type" },
      { Header: "優惠價值", accessor: "value" },
      { Header: "折扣最高上限", accessor: "maxDiscount" },
      { Header: "低消", accessor: "minSpend" },
      { Header: "發放數量", accessor: "maxRedemptions" },
      { Header: "使用數量", accessor: "uses" },
      {
        Header: "開始時間",
        accessor: "validFrom",
        Cell: ({ value }) => formatDisplayDateTime(value),
      },
      {
        Header: "結束時間",
        accessor: "validUntil",
        Cell: ({ value }) => formatDisplayDateTime(value),
      },

      {
        Header: "發放優惠券",
        accessor: "actions",
        Cell: (props) => <IssueButtonCell {...props} onIssue={handleOpenIssueDialog} />,
      },
      { Header: "適用場域", accessor: "venueNames" },
      { Header: "適用機台", accessor: "machineNames" },
      { Header: "適用商品", accessor: "productNames" },
      { Header: "標籤", accessor: "tags" },
      {
        Header: "是否啟用",
        accessor: "isActive",
        Cell: ({ value }) =>
          value === true || value === "true" || value === 1 || value === "1" ? "啟用" : "停用",
      },
    ],
    [handleOpenIssueDialog]
  );

  const codeColumns = React.useMemo(
    () => [
      { Header: "批號", accessor: "batchId" },
      { Header: "優惠碼", accessor: "code" },
      { Header: "使用者", accessor: "usedBy" },
      { Header: "使用時間", accessor: "usedAt" },
      { Header: "優惠券代碼", accessor: "voucherCode" },
      {
        Header: "建立時間",
        accessor: "createdAt",
        Cell: ({ value }) => formatDisplayDateTime(value),
      },
    ],
    []
  );

  const couponRows = React.useMemo(
    () =>
      coupons.map((item) => ({
        ...item,
        canIssueVoucher:
          item?.canIssueVoucher === true ||
          item?.canIssueVoucher === "true" ||
          item?.canIssueVoucher === 1 ||
          item?.canIssueVoucher === "1",
        venueNames: Array.isArray(item.venues)
          ? item.venues
              .map((venue) => venue?.name)
              .filter(Boolean)
              .join(", ") || "-"
          : "-",
        machineNames: Array.isArray(item.machineDetails)
          ? item.machineDetails
              .map((machine) => machine?.machineName || machine?.machine_name || machine?.ref)
              .filter(Boolean)
              .join(", ") || "-"
          : "-",
        productNames: Array.isArray(item.productDetails)
          ? item.productDetails
              .map((product) => product?.displayName || product?.label || product?.ref)
              .filter(Boolean)
              .join(", ") || "-"
          : Array.isArray(item.products)
          ? item.products.join(", ") || "-"
          : item.products || "-",
        tags: Array.isArray(item.tags) ? item.tags.join(", ") : item.tags || "-",
      })),
    [coupons]
  );

  const parseImportFile = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      throw new Error("檔案內容為空");
    }

    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
    if (!Array.isArray(rows) || rows.length === 0) {
      throw new Error("檔案內容為空");
    }

    const headers = Array.isArray(rows[0]) ? rows[0] : [];
    const externalCodeIndex = headers.findIndex(
      (header) => normalizeImportHeader(header) === "externalcode"
    );
    if (externalCodeIndex === -1) {
      throw new Error("找不到 external_code 欄位");
    }

    const nextCodes = [];
    const seen = new Map();

    rows.slice(1).forEach((row, index) => {
      const rawValue = Array.isArray(row) ? row[externalCodeIndex] : undefined;
      const externalCode = String(rawValue || "").trim();
      if (!externalCode) return;
      if (seen.has(externalCode)) {
        throw new Error(
          `檔案內有重複 external_code：${externalCode}（第 ${seen.get(externalCode)} 與 ${
            index + 2
          } 列）`
        );
      }
      seen.set(externalCode, index + 2);
      nextCodes.push({ externalCode });
    });

    if (!nextCodes.length) {
      throw new Error("檔案中沒有可匯入的 external_code");
    }

    return nextCodes;
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const codes = await parseImportFile(file);
      setImportForm((prev) => ({ ...prev, codes }));
      setImportFileName(file.name);
      showSnackbar(`已載入 ${codes.length} 筆第三方優惠券序號`, "success");
    } catch (error) {
      setImportForm((prev) => ({ ...prev, codes: [] }));
      setImportFileName("");
      showSnackbar(error.message || "檔案解析失敗", "error");
    }
  };

  const handleSubmit = async () => {
    const requiredFields = ["code", "name", "value", "minSpend", "validFrom", "validUntil"];

    const missingField = requiredFields.find((field) => !String(form[field] ?? "").trim());
    if (missingField) {
      showSnackbar("請完整填寫所有必填欄位", "warning");
      return;
    }

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      type: "cash",
      value: Number(form.value),
      minSpend: Number(form.minSpend),
      isActive: Boolean(form.isActive),
      validFrom: formatDateTime(form.validFrom.trim()),
      validUntil: formatDateTime(form.validUntil.trim()),
      tags: form.tags
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };

    if (form.distributionType.trim()) {
      payload.distributionType = form.distributionType.trim();
    }
    if (String(form.maxDiscount).trim() !== "") {
      payload.maxDiscount = Number(form.maxDiscount);
    }
    if (String(form.maxRedemptions).trim() !== "") {
      payload.maxRedemptions = Number(form.maxRedemptions);
    }
    if (form.products.length) {
      payload.products = form.products;
    }
    if (form.venueIds.length) {
      payload.venueIds = form.venueIds;
    }
    if (form.machineIds.length) {
      payload.machineIds = form.machineIds;
    }

    if (
      Number.isNaN(payload.value) ||
      Number.isNaN(payload.minSpend) ||
      ("maxDiscount" in payload && Number.isNaN(payload.maxDiscount)) ||
      ("maxRedemptions" in payload && Number.isNaN(payload.maxRedemptions))
    ) {
      showSnackbar("金額或數量欄位需為數字", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: COUPON_CREATE_MUTATION,
          variables: { input: payload },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API 回應錯誤：${response.status}`);
      }

      const data = await response.json();
      if (data?.errors?.length) {
        throw new Error(data.errors[0]?.message || "建立優惠券失敗");
      }
      const result = data?.data?.CreateCoupon;
      const success =
        result?.code === 200 ||
        result?.code === "200" ||
        result?.code === 0 ||
        result?.code === "0";
      if (!success) {
        throw new Error(result?.message || "建立優惠券失敗");
      }
      showSnackbar("優惠券建立成功", "success");
      setForm(initialFormState);
      setOpenCreateDialog(false);
      fetchCoupons();
    } catch (error) {
      console.error("Create coupon failed:", error);
      showSnackbar(`建立優惠券失敗：${error.message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!email || !mdmAuthKey) {
      showSnackbar("缺少登入資訊，無法匯入第三方優惠券", "warning");
      return;
    }
    const requiredFields = [
      "externalCampaignId",
      "name",
      "value",
      "minSpend",
      "validFrom",
      "validUntil",
    ];
    const missingField = requiredFields.find((field) => !String(importForm[field] ?? "").trim());
    if (missingField) {
      showSnackbar("請完整填寫第三方匯入必填欄位", "warning");
      return;
    }
    if (!importForm.codes.length) {
      showSnackbar("請先上傳 CSV / Excel 檔案", "warning");
      return;
    }

    const payload = {
      email,
      externalCampaignId: importForm.externalCampaignId.trim(),
      distributionType: importForm.distributionType.trim() || undefined,
      name: importForm.name.trim(),
      type: "cash",
      value: Number(importForm.value),
      minSpend: Number(importForm.minSpend),
      isActive: Boolean(importForm.isActive),
      validFrom: formatDateTime(importForm.validFrom.trim()),
      validUntil: formatDateTime(importForm.validUntil.trim()),
      codes: importForm.codes,
    };

    if (String(importForm.maxRedemptions).trim() !== "") {
      payload.maxRedemptions = Number(importForm.maxRedemptions);
    }
    if (importForm.venueIds.length) {
      payload.venueIds = importForm.venueIds;
    }
    if (importForm.machineIds.length) {
      payload.machineIds = importForm.machineIds;
    }
    if (importForm.products.length) {
      payload.products = importForm.products;
    }
    if (String(importForm.tags).trim()) {
      payload.tags = importForm.tags
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (String(importForm.remark).trim()) {
      payload.remark = importForm.remark.trim();
    }

    if (
      Number.isNaN(payload.value) ||
      Number.isNaN(payload.minSpend) ||
      ("maxRedemptions" in payload && Number.isNaN(payload.maxRedemptions))
    ) {
      showSnackbar("金額或次數欄位需為數字", "warning");
      return;
    }

    setImportSubmitting(true);
    try {
      const response = await fetch(`${CRM_API_BASE_URL}/api/v1/voucher/import/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || Number(result?.code) !== 200) {
        throw new Error(result?.message || `匯入失敗 (${response.status})`);
      }

      const importedCount = Number(result?.data?.importedCount || importForm.codes.length);
      showSnackbar(`第三方優惠券匯入成功，共 ${importedCount} 筆`, "success");
      setOpenImportDialog(false);
      resetImportForm();
      fetchCoupons();
      fetchCouponCodes();
    } catch (error) {
      console.error("Import third-party coupons failed:", error);
      showSnackbar(`第三方優惠券匯入失敗：${error.message}`, "error");
    } finally {
      setImportSubmitting(false);
    }
  };

  const handleIssueSubmit = async () => {
    const quantity = Number(issueDialog.amount);
    if (!issueDialog.code || !Number.isFinite(quantity) || quantity <= 0) {
      showSnackbar("請輸入有效的發放數量", "warning");
      return;
    }

    try {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: COUPON_ISSUE_MUTATION,
          variables: { input: { code: issueDialog.code, quantity } },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API 回應錯誤：${response.status}`);
      }

      const data = await response.json();
      if (data?.errors?.length) {
        throw new Error(data.errors[0]?.message || "發放失敗");
      }
      const result = data?.data?.IssueCoupon;
      const success =
        result?.code === 200 ||
        result?.code === "200" ||
        result?.code === 0 ||
        result?.code === "0";
      if (!success) {
        throw new Error(result?.message || "發放失敗");
      }

      if (response.status === 200 && Array.isArray(result?.body)) {
        downloadIssuedCouponXlsx(issueDialog.code, result.body);
      }

      showSnackbar("發放成功，下載即將開始", "success");
      setIssueDialog({ open: false, code: "", amount: "" });
      fetchCoupons();
    } catch (error) {
      console.error("Issue coupon failed:", error);
      showSnackbar(`發放失敗：${error.message}`, "error");
    }
  };

  const handleResetFilters = () => {
    setFilters(initialFilterState);
  };

  const handleResetCodeFilters = () => {
    setCodeFilters(initialCodeFilterState);
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, severity, message });
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.couponManagement")}
                </MDTypography>
                <MDBox display="flex" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleOpenImportDialog}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    匯入第三方優惠券序號
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleOpenDialog}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    {t("actions.createCoupon")}
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={3} px={0} width="100%">
                <MDBox px={3} pb={2}>
                  <MDTypography variant="h6">優惠券列表</MDTypography>
                </MDBox>
                <MDBox px={3} pb={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="開始時間"
                        name="validFrom"
                        type="datetime-local"
                        value={filters.validFrom}
                        onChange={handleFilterChange}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="結束時間"
                        name="validUntil"
                        type="datetime-local"
                        value={filters.validUntil}
                        onChange={handleFilterChange}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3} display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filters.isActiveOnly}
                            onChange={handleFilterToggle}
                            name="isActiveOnly"
                          />
                        }
                        label="是否啟用"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <Select
                          name="orderBy"
                          value={filters.orderBy}
                          onChange={handleFilterChange}
                          displayEmpty
                        >
                          {ORDER_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="頁數"
                        name="page"
                        value={filters.page}
                        onChange={handleFilterChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="每頁筆數"
                        name="size"
                        value={filters.size}
                        onChange={handleFilterChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3} display="flex" gap={1} alignItems="center">
                      <Button
                        variant="contained"
                        onClick={fetchCoupons}
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "#ffffff",
                          "&:hover": { backgroundColor: "#115293" },
                        }}
                      >
                        查詢
                      </Button>
                      <Button variant="outlined" onClick={handleResetFilters}>
                        重設
                      </Button>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox px={2} pb={3}>
                  {loadingCoupons && (
                    <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                      <CircularProgress />
                    </MDBox>
                  )}
                  {!loadingCoupons && couponError && (
                    <MDBox py={3} px={3}>
                      <MDTypography variant="body2" color="error">
                        {couponError}
                      </MDTypography>
                    </MDBox>
                  )}
                  {!loadingCoupons && !couponError && (
                    <DataTable
                      table={{ columns: couponColumns, rows: couponRows }}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  )}
                </MDBox>

                <MDBox px={3} pb={2}>
                  <MDTypography variant="h6">優惠碼查詢</MDTypography>
                </MDBox>
                <MDBox px={3} pb={2}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="優惠碼清單 (逗號分隔)"
                        name="codes"
                        value={codeFilters.codes}
                        onChange={handleCodeFilterChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="開始時間"
                        name="start"
                        value={codeFilters.start}
                        onChange={handleCodeFilterChange}
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="結束時間"
                        name="end"
                        value={codeFilters.end}
                        onChange={handleCodeFilterChange}
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <Select
                          name="orderBy"
                          value={codeFilters.orderBy}
                          onChange={handleCodeFilterChange}
                        >
                          {CODE_ORDER_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="頁數"
                        name="page"
                        value={codeFilters.page}
                        onChange={handleCodeFilterChange}
                        type="number"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="每頁筆數"
                        name="size"
                        value={codeFilters.size}
                        onChange={handleCodeFilterChange}
                        type="number"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4} display="flex" gap={1} alignItems="center">
                      <Button
                        variant="contained"
                        onClick={fetchCouponCodes}
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "#ffffff",
                          "&:hover": { backgroundColor: "#115293" },
                        }}
                      >
                        查詢
                      </Button>
                      <Button variant="outlined" onClick={handleResetCodeFilters}>
                        重設
                      </Button>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox px={2} pb={3}>
                  {loadingCodes && (
                    <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                      <CircularProgress />
                    </MDBox>
                  )}
                  {!loadingCodes && codeError && (
                    <MDBox py={3} px={3}>
                      <MDTypography variant="body2" color="error">
                        {codeError}
                      </MDTypography>
                    </MDBox>
                  )}
                  {!loadingCodes && !codeError && (
                    <DataTable
                      table={{ columns: codeColumns, rows: codeRows }}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openCreateDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("actions.createCoupon")}</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} sx={createCouponFormSx}>
            {voucherOptionsError ? <Alert severity="warning">{voucherOptionsError}</Alert> : null}
            <TextField
              label="優惠券代碼"
              name="code"
              value={form.code}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="優惠券名稱"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="優惠券類型"
              name="type"
              value="現金券 (cash)"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="優惠券值"
              name="value"
              value={form.value}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="最低消費金額"
              name="minSpend"
              value={form.minSpend}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="最高折抵金額"
              name="maxDiscount"
              value={form.maxDiscount}
              onChange={handleChange}
              type="number"
              fullWidth
              helperText="現金券可留空"
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={handleToggle} name="isActive" />}
              label="是否啟用"
            />
            <TextField
              label="最大使用次數"
              name="maxRedemptions"
              value={form.maxRedemptions}
              onChange={handleChange}
              type="number"
              fullWidth
            />
            <TextField
              label="開始時間"
              helperText="請選擇開始時間"
              name="validFrom"
              value={form.validFrom}
              onChange={handleChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="結束時間"
              helperText="請選擇結束時間"
              name="validUntil"
              value={form.validUntil}
              onChange={handleChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              multiple
              options={venueOptions}
              value={selectedVenueOptions}
              onChange={handleMultiSelectChange("venueIds")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇適用場域"
                  placeholder={selectedVenueOptions.length ? "" : "不限場域則留空"}
                />
              )}
            />
            <Autocomplete
              multiple
              options={machineOptions}
              value={selectedMachineOptions}
              onChange={handleMultiSelectChange("machineIds")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇適用機台"
                  placeholder={selectedMachineOptions.length ? "" : "不限機台則留空"}
                />
              )}
            />
            <Autocomplete
              multiple
              options={productOptions}
              value={selectedProductOptions}
              onChange={handleMultiSelectChange("products")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇適用產品"
                  placeholder={selectedProductOptions.length ? "" : "不限商品則留空"}
                />
              )}
            />
            <TextField
              label="標籤 (以逗號分隔)"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              fullWidth
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              backgroundColor: "#FF9800",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#FB8C00" },
            }}
          >
            {submitting ? "送出中..." : "建立"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openImportDialog} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>匯入第三方優惠券序號</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} sx={createCouponFormSx}>
            {voucherOptionsError ? <Alert severity="warning">{voucherOptionsError}</Alert> : null}
            <TextField
              label="優惠券活動代碼"
              name="externalCampaignId"
              value={importForm.externalCampaignId}
              onChange={handleImportChange}
              required
              fullWidth
            />
            <TextField
              label="優惠券名稱"
              name="name"
              value={importForm.name}
              onChange={handleImportChange}
              required
              fullWidth
            />
            <TextField
              label="券型"
              value="現金券 (cash)"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="優惠券值"
              name="value"
              value={importForm.value}
              onChange={handleImportChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="最低消費金額"
              name="minSpend"
              value={importForm.minSpend}
              onChange={handleImportChange}
              required
              type="number"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={importForm.isActive}
                  onChange={handleImportToggle}
                  name="isActive"
                />
              }
              label="是否啟用"
            />
            <TextField
              label="每張券可使用次數"
              name="maxRedemptions"
              value={importForm.maxRedemptions}
              onChange={handleImportChange}
              type="number"
              fullWidth
            />
            <TextField
              label="開始時間"
              name="validFrom"
              value={importForm.validFrom}
              onChange={handleImportChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="結束時間"
              name="validUntil"
              value={importForm.validUntil}
              onChange={handleImportChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              multiple
              options={venueOptions}
              value={selectedImportVenueOptions}
              onChange={handleImportMultiSelectChange("venueIds")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇限制場域"
                  placeholder={selectedImportVenueOptions.length ? "" : "不限場域則留空"}
                />
              )}
            />
            <Autocomplete
              multiple
              options={importMachineOptions}
              value={selectedImportMachineOptions}
              onChange={handleImportMultiSelectChange("machineIds")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇限制機台"
                  placeholder={selectedImportMachineOptions.length ? "" : "不限機台則留空"}
                />
              )}
            />
            <Autocomplete
              multiple
              options={productOptions}
              value={selectedImportProductOptions}
              onChange={handleImportMultiSelectChange("products")}
              loading={loadingVoucherOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="選擇限制商品"
                  placeholder={selectedImportProductOptions.length ? "" : "不限商品則留空"}
                />
              )}
            />
            <TextField
              label="標籤 (以逗號分隔)"
              name="tags"
              value={importForm.tags}
              onChange={handleImportChange}
              fullWidth
            />
            <TextField
              label="備註"
              name="remark"
              value={importForm.remark}
              onChange={handleImportChange}
              fullWidth
              multiline
              minRows={2}
            />
            <MDBox
              sx={{
                border: "1px dashed #bdbdbd",
                borderRadius: 2,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MDTypography variant="button" fontWeight="medium">
                匯入檔案
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                支援 `.csv`、`.xlsx`、`.xls`，欄位需包含 `external_code`
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Button
                  component="label"
                  variant="contained"
                  sx={{
                    backgroundColor: "#FF9800",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    px: 2.5,
                    py: 1,
                    "&:hover": { backgroundColor: "#FB8C00" },
                  }}
                >
                  上傳 CSV / Excel
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleImportFileChange}
                  />
                </Button>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  {importFileName || "尚未選擇檔案"}
                </MDTypography>
              </MDBox>
              <MDTypography variant="button" color="text" fontWeight="medium">
                已載入序號數：{importForm.codes.length}
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importSubmitting}>
            取消
          </Button>
          <Button
            onClick={handleImportSubmit}
            disabled={importSubmitting}
            sx={{
              backgroundColor: "#FF9800",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#FB8C00" },
            }}
            variant="contained"
          >
            {importSubmitting ? "匯入中..." : "開始匯入"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issueDialog.open} onClose={handleCloseIssueDialog} maxWidth="xs" fullWidth>
        <DialogTitle>發放優惠券</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="優惠券代碼"
              value={issueDialog.code}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="數量"
              value={issueDialog.amount}
              onChange={handleIssueAmountChange}
              type="number"
              fullWidth
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIssueDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleIssueSubmit}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CouponManagementBK;
